/**
 * 鼠标坐标定位的右键菜单（DESIGN.md §7.1 补充，参照 Photoshop 图层面板
 * 右键菜单）。
 *
 * 跟 `MenuBar.tsx` 是两种定位机制：那边锚定在菜单栏按钮下方（CSS 相对
 * 定位即可），这里要锚定在鼠标点击处（任意坐标，且不能超出视口）。
 * 项的渐染/子菜单展开这两块逻辑跟 `MenuBar.tsx` 同构，但没有抽公共
 * 组件——`MenuBar` 认识 `MenuGroupModel`（跟动作注册表的分组耦合），
 * 这里只是一份任意的、调用方自己拼的条目列表，勉强共用反而绕。
 *
 * 范围上比 `MenuBar` 简化一档：没有键盘上下移动高亮（只有 Esc 关闭 +
 * 点击外部关闭 + 子菜单划过展开），右键菜单本身就是鼠标交互为主的场景，
 * 键盘导航收益不成比例。
 */
import type { JSX } from 'solid-js';
import { For, Show, createSignal, onCleanup, onMount } from 'solid-js';
import styles from './ContextMenu.module.css';

export interface ContextMenuItem {
  readonly id: string;
  readonly title: string;
  /** 缺省可用。 */
  readonly enabled?: boolean;
  /** 这一项之前要不要画分隔线。 */
  readonly separatorBefore?: boolean;
  /** 存在即为二级菜单父项，本身不可执行。 */
  readonly submenu?: readonly ContextMenuItem[];
}

export interface ContextMenuProps {
  /** 触发点的视口坐标（一般是 `event.clientX/Y`）。 */
  readonly x: number;
  readonly y: number;
  readonly items: readonly ContextMenuItem[];
  readonly onExecute: (id: string) => void;
  readonly onClose: () => void;
}

/**
 * 二级子菜单浮层（拆成独立组件而不是内联在 `<For>` 里，是因为它自己要用
 * `onMount` 量尺寸、`createSignal` 记算出来的位置——内联成 `<Show>` 的
 * 渲染回调在 TS 类型层面通不过：`when` 传的是布尔值而不是"要传给回调的那个
 * 值"，Solid 的 `<Show>` 类型只在后一种用法下才允许函数 children）。
 *
 * 原来纯靠 CSS 的 `inset-inline-start: 100%` 把子菜单摆在触发行右边，从不
 * 检查是否超出视口——真实 bug：图层面板本身贴在屏幕右侧，混合模式这种
 * 条目又多的子菜单一展开必然更往右，几乎每次都会被截到窗口外面，表现为
 * "看不见"。照抄根菜单（`ContextMenu` 自身）的做法：先按 CSS 默认位置
 * 渲染出来量一次尺寸，装不下就整体挪到触发行左边，纵向也做一次夹取。
 */
function Submenu(props: {
  readonly items: readonly ContextMenuItem[];
  readonly parentId: string;
  readonly onExecuteItem: (item: ContextMenuItem) => void;
}): JSX.Element {
  const [pos, setPos] = createSignal<{ left: number; top: number } | null>(null);
  let subEl: HTMLDivElement | undefined;

  onMount(() => {
    if (subEl === undefined) return;
    const wrapper = subEl.parentElement;
    if (wrapper === null) return;
    const anchorRect = wrapper.getBoundingClientRect();
    const subRect = subEl.getBoundingClientRect();
    const margin = 4;
    let left = anchorRect.right;
    if (left + subRect.width > window.innerWidth - margin) {
      left = anchorRect.left - subRect.width;
    }
    left = Math.max(margin, Math.min(left, window.innerWidth - subRect.width - margin));
    let top = anchorRect.top;
    if (top + subRect.height > window.innerHeight - margin) {
      top = Math.max(margin, window.innerHeight - subRect.height - margin);
    }
    setPos({ left, top });
  });

  return (
    <div
      ref={(el) => {
        subEl = el;
      }}
      class={styles.submenuFlyout}
      role="menu"
      data-testid={`context-menu-submenu-${props.parentId}`}
      style={pos() !== null ? { position: 'fixed', left: `${pos()!.left}px`, top: `${pos()!.top}px` } : undefined}
    >
      <For each={props.items}>
        {(sub) => (
          <>
            <Show when={sub.separatorBefore === true}>
              <div class={styles.separator} role="separator" />
            </Show>
            <button
              type="button"
              role="menuitem"
              class={styles.item}
              data-testid={`context-menu-item-${sub.id}`}
              aria-disabled={sub.enabled === false}
              disabled={sub.enabled === false}
              onClick={() => {
                props.onExecuteItem(sub);
              }}
            >
              <span>{sub.title}</span>
            </button>
          </>
        )}
      </For>
    </div>
  );
}

export function ContextMenu(props: ContextMenuProps): JSX.Element {
  let rootEl: HTMLDivElement | undefined;
  const [openSubmenu, setOpenSubmenu] = createSignal<string | null>(null);
  const [pos, setPos] = createSignal<{ left: number; top: number }>({ left: props.x, top: props.y });

  const execute = (item: ContextMenuItem): void => {
    if (item.enabled === false) return;
    props.onClose();
    props.onExecute(item.id);
  };

  onMount(() => {
    // 渲染出实际尺寸之后才知道会不会溢出视口，夹一下坐标——
    // 首帧短暂出现在原始坐标再挪一次，比预先猜测尺寸更准确也更简单
    if (rootEl !== undefined) {
      const rect = rootEl.getBoundingClientRect();
      const maxLeft = Math.max(0, window.innerWidth - rect.width - 4);
      const maxTop = Math.max(0, window.innerHeight - rect.height - 4);
      setPos({ left: Math.min(props.x, maxLeft), top: Math.min(props.y, maxTop) });
    }
    const onPointerDown = (event: PointerEvent): void => {
      if (rootEl !== undefined && event.target instanceof Node && !rootEl.contains(event.target)) {
        props.onClose();
      }
    };
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.preventDefault();
        props.onClose();
      }
    };
    // contextmenu 事件本身也会冒泡到 window——挡一下，否则右键菜单刚弹出
    // 就被这次触发它的同一个事件（在别的浏览器里表现为 pointerdown）关掉
    window.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('keydown', onKeyDown, true);
    onCleanup(() => {
      window.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('keydown', onKeyDown, true);
    });
  });

  return (
    <div
      ref={(el) => {
        rootEl = el;
      }}
      class={styles.menu}
      role="menu"
      data-testid="context-menu"
      style={{ left: `${pos().left}px`, top: `${pos().top}px` }}
    >
      <For each={props.items}>
        {(item) => (
          <>
            <Show when={item.separatorBefore === true}>
              <div class={styles.separator} role="separator" />
            </Show>
            <Show
              when={item.submenu === undefined}
              fallback={
                <div
                  class={styles.submenuWrapper}
                  onPointerEnter={() => {
                    setOpenSubmenu(item.id);
                  }}
                >
                  <button
                    type="button"
                    role="menuitem"
                    aria-haspopup="menu"
                    aria-expanded={openSubmenu() === item.id}
                    class={styles.item}
                    data-testid={`context-menu-item-${item.id}`}
                    aria-disabled={item.enabled === false}
                    disabled={item.enabled === false}
                  >
                    <span>{item.title}</span>
                    <span class={styles.arrow}>▶</span>
                  </button>
                  <Show when={openSubmenu() === item.id}>
                    <Submenu items={item.submenu ?? []} parentId={item.id} onExecuteItem={execute} />
                  </Show>
                </div>
              }
            >
              <button
                type="button"
                role="menuitem"
                class={styles.item}
                data-testid={`context-menu-item-${item.id}`}
                aria-disabled={item.enabled === false}
                disabled={item.enabled === false}
                onPointerEnter={() => {
                  setOpenSubmenu(null);
                }}
                onClick={() => {
                  execute(item);
                }}
              >
                <span>{item.title}</span>
              </button>
            </Show>
          </>
        )}
      </For>
    </div>
  );
}
